/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Normalizes a typed share percentage.
 *
 * Both separators are accepted because both get typed -- "2,4" from a pt-BR
 * keyboard and "2.4" from muscle memory -- and rejecting either would be a
 * gratuitous failure. Everything that is not a digit or a separator is
 * dropped as it is typed, so the field cannot hold letters; the first
 * separator wins, so "2.4.5" cannot happen.
 */
export function maskPercent(value: string): string {
  const cleaned = value.replace(/[^\d.,]/g, '').replace(/,/g, '.');
  const [whole, ...rest] = cleaned.split('.');
  const decimals = rest.join('').slice(0, 2);
  const clampedWhole = whole.slice(0, 3);
  if (!rest.length) return clampedWhole;
  return `${clampedWhole}.${decimals}`;
}

/**
 * Parses a normalized percentage into basis points.
 *
 * Rounds because two decimals is the finest split the field accepts and
 * `share_bps` is an integer: `33.34` -> `3334`. Unparseable input yields 0 so
 * the 100%-total check reports a wrong sum instead of throwing.
 */
export function percentToBps(percent: string): number {
  const parsed = Number(String(percent).replace(',', '.'));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

/**
 * Converts `moneyMask`'s unmasked output into integer cents.
 *
 * `moneyMask` hands back a `toFixed(2)` string such as `"3100.00"`, so the
 * multiplication is exact to the cent; rounding only guards against float
 * representation drift.
 */
export function moneyToCents(unmasked: string): number {
  const parsed = Number(unmasked);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}
